import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { X } from "react-feather";

import { getFileSize } from "../../../../utils/fileAttaches";

import "./internal-attachments.scss";

/** Cria uma URL de objeto para o arquivo local e a revoga ao desmontar/trocar.
 *  Sem o revoke, cada arquivo selecionado vazaria um blob até o reload da página.
 */
const useObjectUrl = (file) => {
    const [objectUrl, setObjectUrl] = useState(null);

    useEffect(() => {
        if (!file) return undefined;
        const url = URL.createObjectURL(file);
        setObjectUrl(url);
        return () => {
            URL.revokeObjectURL(url);
            setObjectUrl(null);
        }
    }, [file]);

    return objectUrl;
}

/** Ícone do formato, com os mesmos SVGs usados pelas mensagens com clientes.
 *  `require` dinâmico com fallback: um formato fora da lista cai no Default.
 */
export const FileFormatIcon = ({ fileName, className }) => {
    const extension = (fileName || "").split(".").pop().toUpperCase();
    let icon;
    try {
        icon = require(`../../../../assets/img/svg/mimetype/${extension}.svg`);
    } catch (e) {
        icon = require("../../../../assets/img/svg/mimetype/Default.svg");
    }
    return <img className={className} src={icon} alt={extension} />;
}

export const InternalFilePreview = ({ file }) => {
    const intl = useIntl();
    const objectUrl = useObjectUrl(file);

    if (!file) return null;

    const isImage = (file.type || "").startsWith("image/");
    const isVideo = (file.type || "").startsWith("video/");
    const isPdf = file.type === "application/pdf";

    if (objectUrl && isImage) {
        return (
            <div className="internal-file-preview-ui">
                <img className="internal-file-preview-media" src={objectUrl} alt={file.name} />
            </div>
        );
    }

    if (objectUrl && isVideo) {
        return (
            <div className="internal-file-preview-ui">
                <video className="internal-file-preview-media" src={objectUrl} controls />
            </div>
        );
    }

    if (objectUrl && isPdf) {
        return (
            <div className="internal-file-preview-ui">
                <iframe className="internal-file-preview-pdf" title={file.name} src={objectUrl} frameBorder="0" />
            </div>
        );
    }

    return (
        <div className="internal-file-preview-ui internal-file-preview-empty">
            <FileFormatIcon fileName={file.name} className="internal-file-preview-icon" />
            <span className="internal-file-preview-title">
                {intl.formatMessage({ id: "Pré-visualização não disponível" })}
            </span>
            <span className="internal-file-preview-info">
                {getFileSize(file.size)} - {file.name}
            </span>
        </div>
    );
}

export const InternalFileThumbnail = ({ file, active, onSelectFile, onRemoveFile }) => {
    const objectUrl = useObjectUrl(file);
    const isImage = (file.type || "").startsWith("image/");

    return (
        <div
            className={`internal-file-thumbnail-ui ${active ? "active" : ""}`}
            onClick={onSelectFile}
            title={file.name}
        >
            {objectUrl && isImage
                ? <img className="internal-file-thumbnail-image" src={objectUrl} alt={file.name} />
                : <FileFormatIcon fileName={file.name} className="internal-file-thumbnail-icon" />
            }
            <span
                className="internal-file-thumbnail-remove"
                role="button"
                onClick={(e) => {
                    /** O clique de remover não pode também selecionar o arquivo. */
                    e.stopPropagation();
                    onRemoveFile();
                }}
            >
                <X size={14} />
            </span>
        </div>
    );
}
